import React, { useMemo } from 'react'
import { useBrowser, CustomMediaBreakpoints } from '@s4l/utils/hooks/useBrowser'

import styles from './index.module.css'

export interface GridLayoutColumnBreakpointConfig {
  breakpoint: CustomMediaBreakpoints
  column: number
}

export interface GridLayoutMaxItemBreakpointConfig {
  breakpoint: CustomMediaBreakpoints
  maxItem: number
}

export interface GridLayoutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'style'> {
  children?: React.ReactNode
  columns?: GridLayoutColumnBreakpointConfig[]
  maxItems?: GridLayoutMaxItemBreakpointConfig[]
  gap?: string
}

function getColumnsFromBreakpoints(
  breakpoints: GridLayoutColumnBreakpointConfig[] | undefined,
  breakpoint: CustomMediaBreakpoints,
  fallback: number
) {
  return breakpoints?.find(b => b.breakpoint === breakpoint)?.column ?? fallback
}

function getMaxItemsFromBreakpoints(
  breakpoints: GridLayoutMaxItemBreakpointConfig[] | undefined,
  breakpoint: CustomMediaBreakpoints
) {
  return breakpoints?.find(b => b.breakpoint === breakpoint)?.maxItem
}

function GridLayout({
  children,
  columns,
  maxItems,
  gap,
  className,
  ...rest
}: Readonly<GridLayoutProps>) {
  const { activeCustomMedia } = useBrowser()

  const maxItem = getMaxItemsFromBreakpoints(maxItems, activeCustomMedia)

  const nextChildren = useMemo(() => {
    const items = React.Children.toArray(children)
    if (maxItem === undefined) return items
    return items.slice(0, Math.max(0, maxItem))
  }, [children, maxItem])

  return (
    <div
      className={className ? `${styles.gridLayout} ${className}` : styles.gridLayout}
      style={{
        ['--grid-layout-columns-mobile' as string]: getColumnsFromBreakpoints(columns, CustomMediaBreakpoints.Mobile, 1), // prettier-ignore
        ['--grid-layout-columns-mobile-l' as string]: getColumnsFromBreakpoints(columns, CustomMediaBreakpoints.MobileL, getColumnsFromBreakpoints(columns, CustomMediaBreakpoints.Mobile, 1)), // prettier-ignore
        ['--grid-layout-columns-tablet' as string]: getColumnsFromBreakpoints(columns, CustomMediaBreakpoints.Tablet, 2), // prettier-ignore
        ['--grid-layout-columns-desktop' as string]: getColumnsFromBreakpoints(columns, CustomMediaBreakpoints.Desktop, 3), // prettier-ignore
        ['--grid-layout-gap' as string]: gap,
      }}
      {...rest}
    >
      {nextChildren}
    </div>
  )
}

GridLayout.displayName = 'GridLayout'
export default GridLayout
